import React, { useCallback, useState, useEffect, useMemo } from 'react'

import { DatePicker, Alert } from '@refera/ui-web'

import useStyles from '_views/triage-view/styles'
import { navigate } from '@reach/router'
import {
  Grid,
  InputLabel,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  IconButton,
  FormHelperText,
  SwipeableDrawer,
  Divider,
  Slide,
} from '@material-ui/core'
import Theme from '@refera/ui-core'
import { Edit2, CloseCircle } from '@refera/ui-icons'

import { Controller, useFormContext } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { updateProperty } from '_modules/property/actions'

import { BUTTONS_ACTION } from '_utils/constants/service-order'
import { CPF_CNPJ_REGEX, EMAIL_REGEX, PHONE_REGEX, ROUTES } from '_utils/constants'
import {
  currentServiceOrderSelector,
  agencyServiceOrderFlows,
  getCameFromSelectTradesmanSelector,
  getTriageAIConfidenceThresholdsSelector,
} from '_modules/service-orders/selectors'
import {
  updateServiceOrderFlowsOptions,
  getOptionsPaymentResponsible,
  getServiceOrder,
  getCategoryList,
  getTriageAIConfidenceThresholds,
  aiTriage,
  setFromSelectTradesman,
  setOpenProviderModal,
  setOpenDialogFromFlow,
  getActiveBudget,
} from '_/modules/service-orders/actions'
import useCanDoButtonAction from '_/hooks/use-can-do-button-action'
import { cpfCnpjMask, parseToNumber, validationCNPJ, validationCPF } from '_utils/helpers'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'

import { formatErrorMessage } from '_views/finance/utils/FormatErrorMessage'
import { BasicInput, MaskBasicInput, Checkbox, Select as SelectField } from '_/components/inputs'
import { cpfCnpjMaskSwitch } from '_views/finance/utils/functions'
import { getStepStatusLog } from '_/modules/budget/actions'
import { includeDDI } from '_/views/new-service/utils/includeDDI'

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import useRolePermission from '_hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

import * as Modal from '_components/modal/generic-modal'
import MultiSelectedTree from '_/components/multi-selected-tree-view'
import Select from '_components/select'
import moment from 'moment/moment'
import { getBusinessFronts } from '_/modules/businessFront/actions'
import { businessFrontListSelector } from '_/modules/businessFront/selectors'
import { ERRORS } from '_/views/finance/payer-data/utils'
import Attachments from '_/components/refera-ui/Attachments'
import PreviewComponent from '_/components/common/preview-component'
import { useTriageForm } from './use-triage-form'
import {
  getAISuggestionCategoryNames,
  getAISuggestionConfidence,
  getAISuggestionReasoningSummary,
  getAISuggestionTriggers,
  getAISuggestionValue,
  getConfidenceBadgeConfig,
} from './confidence-utils'

const FORM_ERROR_MESSAGE = 'Um ou mais dados do formulário possuem problemas.'
const DEFAULT_PREVIEW_STATE = {
  open: false,
  selectedIndex: undefined,
}
const DEFAULT_AI_EXPLANATION_STATE = {
  open: false,
  fieldLabel: '',
  suggestedValue: '',
  confidenceLabel: '',
  backgroundColor: '#6B7280',
  textColor: '#FFFFFF',
  triggers: [],
  reasoningSummary: '',
}
const AIExplanationTransition = React.forwardRef((props, ref) => (
  <Slide direction="right" ref={ref} {...props} />
))
const MEDIA_ACCEPT = {
  'image/*': ['.jpeg', '.png', '.jpg', '.bmp', '.webp'],
  'video/*': ['.mkv', '.mp4', '.mov', '.avi', '.m4v', '.mpg', '.mpeg', '.wmv', '.webm'],
}
const PDF_ACCEPT = {
  'application/pdf': ['.pdf'],
}

export const validateCPFCNPJ = value => {
  const parseNumber = parseToNumber(value)

  if (parseNumber.length === 0) return true
  if (parseNumber.length <= 11 && !validationCPF(value)) return ERRORS.INVALID_CPF
  if (parseNumber.length > 11 && parseNumber.length <= 14 && !validationCNPJ(value))
    return ERRORS.INVALID_CNPJ

  return true
}

/**
 * @param {Object} payload
 * @param {String} payload.serviceOrderId
 * @param {Boolean} payload.supressRedirects
 * @param {Function} payload.onSubmitSuccess
 * @param {(payload) => Boolean} payload.isFormValid
 */
const TriageForm = ({
  serviceOrderId,
  supressRedirects = false,
  onSubmitSuccess,
  isFormValid = () => true,
}) => {
  const styles = useStyles()
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const agencyFlows = useSelector(agencyServiceOrderFlows)
  const cameFromSelectTradesman = useSelector(getCameFromSelectTradesmanSelector)
  const businessFrontList = useSelector(businessFrontListSelector)
  const confidenceThresholds = useSelector(getTriageAIConfidenceThresholdsSelector) || []
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()

  const { setIsLoading } = useTriageForm()
  const methods = useFormContext()

  const {
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = methods

  const { checkUserPermission } = useRolePermission()

  const [paymentResponsibleOptions, setPaymentResponsibleOptions] = useState([])
  const [openCategoriesModal, setOpenCategoriesModal] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState([])
  const [categoryList, setCategoryList] = useState()
  const [categoriesToUpdate, setCategoriesToUpdate] = useState([])
  const [textDate, setTextDate] = useState('')
  const [previewState, setPreviewState] = useState(DEFAULT_PREVIEW_STATE)
  const [aiExplanationState, setAiExplanationState] = useState(DEFAULT_AI_EXPLANATION_STATE)

  const priority = useMemo(() => serviceOrder?.priority, [serviceOrder?.priority])
  const serviceOrderData = serviceOrder?.toJS()
  const flow = serviceOrderData?.businessFront || ''
  const aiSuggestions = serviceOrderData?.aiSuggestions
  const suggestedCategoryNames = getAISuggestionCategoryNames(aiSuggestions)
  const suggestedCategories = suggestedCategoryNames.join(', ')
  const suggestedPriorityLabel = getAISuggestionValue(aiSuggestions, 'priority')
  const suggestedBusinessFrontLabel = getAISuggestionValue(aiSuggestions, 'businessFront')
  const suggestedPaymentResponsibleStatus = getAISuggestionValue(
    aiSuggestions,
    'paymentResponsible'
  )
  const categoryConfidence = getAISuggestionConfidence(aiSuggestions, 'category')
  const priorityConfidence = getAISuggestionConfidence(aiSuggestions, 'priority')
  const flowConfidence = getAISuggestionConfidence(aiSuggestions, 'businessFront')
  const paymentResponsibleConfidence = getAISuggestionConfidence(
    aiSuggestions,
    'paymentResponsible'
  )
  const isAITriageOn = serviceOrderData?.agency?.triageAis
  const { agency } = serviceOrder?.toJS() || {}
  const ownerTerm = agency?.ownerTerm?.toLowerCase() || 'proprietário(a)'
  const tenantTerm = agency?.tenantTerm?.toLowerCase() || 'inquilino(a)'

  const canEditPayerResponsible = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.EDIT_PAYER_RESPONSIBLE,
  })

  const propertyInfo = useMemo(() => {
    return serviceOrder?.toJS()?.property
  }, [serviceOrder])

  const [prevPaymentResponsible, setPrevPaymentResponsible] = useState(
    propertyInfo?.paymentResponsible || ''
  )

  const cpfOrCnpjWatch = watch('cpfOrCnpj')
  const tenantCpfOrCnpjWatch = watch('tenantCpfOrCnpj')
  const contractStartDate = watch('triage.contractStartDate')
  const acceptCategory = watch('triage.acceptCategorySuggestion')
  const acceptPriority = watch('triage.acceptPrioritySuggestion')
  const acceptFlow = watch('triage.acceptFlowSuggestion')
  const acceptPaymentResponsible = watch('triage.acceptPaymentResponsibleSuggestion')
  const payer = watch('paymentResponsible')
  const serviceOrderDescription =
    serviceOrder?.get('description') || serviceOrder?.get('originalDescription') || ''

  const getPdfFlag = useCallback(file => {
    if (file instanceof File) {
      return file.type === 'application/pdf'
    }

    if (typeof file?.get?.('isPdf') === 'boolean') return file.get('isPdf')
    if (typeof file?.get?.('is_pdf') === 'boolean') return file.get('is_pdf')
    if (typeof file?.isPdf === 'boolean') return file.isPdf
    if (typeof file?.is_pdf === 'boolean') return file.is_pdf

    const fileUrl = file?.get?.('file') || file?.file
    return typeof fileUrl === 'string' ? fileUrl.toLowerCase().includes('.pdf') : false
  }, [])

  const serviceOrderAttachments = useMemo(
    () =>
      serviceOrder
        ?.get('attachments')
        ?.toArray()
        ?.filter(file => file?.get?.('fileType') === 'pictures_order')
        ?.map(file => ({
          id: file?.get('id'),
          file: file?.get('file'),
          isPdf: getPdfFlag(file),
        })) || [],
    [serviceOrder, getPdfFlag]
  )

  const serviceOrderAttachmentsPdf = useMemo(
    () => serviceOrderAttachments.filter(file => file.isPdf),
    [serviceOrderAttachments]
  )

  const serviceOrderAttachmentsMedia = useMemo(
    () => serviceOrderAttachments.filter(file => !file.isPdf),
    [serviceOrderAttachments]
  )

  const fetchPayerData = useCallback(() => {
    const params = { useAgencyTerms: true }
    dispatch(getOptionsPaymentResponsible(serviceOrderId, params)).then(data => {
      setPaymentResponsibleOptions(data?.options)
    })
  }, [dispatch, serviceOrderId])

  const fetchServiceOrder = useCallback(async () => {
    setIsLoading(true)
    await dispatch(getServiceOrder(serviceOrderId))
      .then(() => {
        setIsLoading(false)
      })
      .catch(() => {
        showToast({ type: 'error' })
        setIsLoading(false)
      })
  }, [serviceOrderId])

  const cleanPayload = data => {
    const payload = { ...data }

    ;[
      ['acceptCategorySuggestion', 'aiCategoryErrorExplanation'],
      ['acceptPrioritySuggestion', 'aiPriorityErrorExplanation'],
      ['acceptFlowSuggestion', 'aiFlowErrorExplanation'],
      ['acceptPaymentResponsibleSuggestion', 'aiPaymentSuggestionErrorExplanation'],
    ].forEach(([acceptKey, errorKey]) => {
      if (payload[acceptKey] === true) {
        delete payload[errorKey]
      }
    })

    const cleaned = Object.fromEntries(
      Object.entries(payload).filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
    )

    return cleaned
  }

  const handleServiceOrderFlow = () => {
    return new Promise(resolve => {
      showDialog({
        subject:
          'Este fluxo vai criar um Orçamento de valor zerado, para ser enviado ao prestador selecionado. Você confirma tal ação?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        type: 'warning',
        onApprove: () => {
          closeDialog()
          resolve(true)
        },
        onCancel: () => {
          closeDialog()
          resolve(false)
        },
      })
    })
  }

  const redirectToSelectProviderModal = useCallback(() => {
    dispatch(setFromSelectTradesman(false))
    dispatch(setOpenProviderModal(true))
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrder.id}`)
  }, [dispatch, navigate, serviceOrder?.id])

  const handleOrderWithoutPriceFlow = useCallback(() => {
    dispatch(getActiveBudget(serviceOrder.id))
    dispatch(setOpenDialogFromFlow(true))
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrder.id}`)
  }, [dispatch, navigate, serviceOrder?.id])

  const canEditPaymentResponsible = useCallback(dataValues => {
    const {
      tenantName,
      tenantEmail,
      tenantWhatsapp,
      propertyEmail,
      propertyName,
      propertyWhatsapp,
      paymentResponsible,
    } = dataValues

    const arePayerDataFilled =
      paymentResponsible === 'owner'
        ? propertyEmail && propertyName && propertyWhatsapp
        : tenantName && tenantEmail && tenantWhatsapp

    return arePayerDataFilled
  }, [])

  const handleSumbissionError = useCallback(
    err => {
      if (err?.message) {
        showDialog({
          labelApprove: 'Ok',
          onApprove: closeDialog,
          subject: 'Atenção!',
          description: err?.message,
          type: 'warning',
        })
        return null
      }

      const errorMessage = formatErrorMessage(err)
      showToast({
        message: errorMessage,
        type: 'error',
      })
      return null
    },
    [showDialog, closeDialog, showToast, formatErrorMessage]
  )

  const onInvalidSubmit = () => {
    showToast({
      type: 'error',
      message: FORM_ERROR_MESSAGE,
    })
  }

  const onSubmit = useCallback(
    async data => {
      const { triage, ...dataValues } = data

      const triageValues = cleanPayload(triage)

      if (Object.keys(errors)?.length) return false

      if (
        flow?.name !== triageValues?.flow &&
        triageValues?.flow === 'service_order_without_price'
      ) {
        const proceed = await handleServiceOrderFlow()
        if (!proceed) {
          return null
        }
      }

      const updateData = {
        ...triageValues,
        ...(categoriesToUpdate?.length > 0 && { category: categoriesToUpdate }),
        ...(priority !== triageValues?.priority && { priority: triageValues?.priority }),
        ...(flow?.name !== triageValues?.flow && { flow: triageValues?.flow }),
        ...(triageValues?.contractStartDate && {
          contractStartDate: moment(triageValues.contractStartDate).format('YYYY-MM-DD'),
        }),
      }

      const responsiblePaymentField = dataValues?.paymentResponsible
      const isNewResponsiblePayment = responsiblePaymentField !== prevPaymentResponsible

      if (isNewResponsiblePayment) {
        if (!canEditPayerResponsible) {
          showToast({
            message: 'A etapa do chamado não permite a edição do responsável pelo pagamento.',
            type: 'error',
          })
          return null
        }
      }

      setIsLoading(true)
      const payload = {
        serviceOrderId,
        serviceUpdateOwner: true,
        ...dataValues,
      }

      // Validate form with custom validator
      if (!isFormValid(updateData)) {
        setIsLoading(false)
        onInvalidSubmit()
        return null
      }

      try {
        setIsLoading(true)
        await dispatch(updateProperty(serviceOrder?.propertyId, payload))
        await dispatch(aiTriage(serviceOrder?.id, updateData))
        const isWaitingPayerData = serviceOrder?.stepStatus === 'waiting_payer_data'

        if (isWaitingPayerData) {
          const arePayerDataFilled = canEditPaymentResponsible(dataValues)

          if (!arePayerDataFilled) {
            showToast({
              message:
                'O chamado ainda ficará na etapa "Aguardando dados do pagador" até que você preencha todos os dados do responsável pelo pagamento.',
              type: 'info',
            })
            setIsLoading(false)
            return null
          }
        }
        methods.reset({ ...data, triage: triageValues })

        showToast({ type: 'success' })
        await fetchServiceOrder()

        if (cameFromSelectTradesman && !supressRedirects) {
          redirectToSelectProviderModal()
          return null
        }

        if (triageValues?.flow === 'service_order_without_price' && !supressRedirects) {
          handleOrderWithoutPriceFlow()
        }

        if (onSubmitSuccess) {
          onSubmitSuccess()
        }
      } catch (err) {
        handleSumbissionError(err)
      } finally {
        fetchServiceOrder()
        setIsLoading(false)
      }

      return null
    },
    [
      serviceOrderId,
      serviceOrder,
      errors,
      canEditPayerResponsible,
      prevPaymentResponsible,
      categoriesToUpdate,
      cameFromSelectTradesman,
      supressRedirects,
      onSubmitSuccess,
      priority,
      flow,
      fetchServiceOrder,
      handleSumbissionError,
      canEditPaymentResponsible,
      handleServiceOrderFlow,
      redirectToSelectProviderModal,
      handleOrderWithoutPriceFlow,
      onInvalidSubmit,
    ]
  )

  useEffect(() => {
    fetchServiceOrder()
    dispatch(getStepStatusLog(serviceOrderId))
    dispatch(getTriageAIConfidenceThresholds()).catch(() => null)
    fetchPayerData()
    dispatch(getCategoryList({ isActive: true, allowsSelection: true })).then(res =>
      setCategoryList(() => res)
    )
  }, [serviceOrderId])

  const handleSelectedItemsChange = selectedItems => {
    setSelectedCategories(selectedItems)
  }

  const suggestedPriority = useMemo(() => {
    const priorityMap = {
      Emergência: 3,
      Urgente: 2,
      Normal: 1,
    }
    return priorityMap[suggestedPriorityLabel] ?? null
  }, [suggestedPriorityLabel])

  const notShowValue = useMemo(() => {
    const step = serviceOrder?.get('stepStatus')
    const stepStatusWhichCannotShowFlows = ['triage', 'waiting_payer_data']
    const canShowFlows = ['notice_to_owner', 'notice_tenant']
    return canShowFlows.includes(flow.name) ? false : stepStatusWhichCannotShowFlows.includes(step)
  }, [serviceOrder, flow])

  const suggestedFlow = useMemo(() => {
    const item = agencyFlows?.find(i => i?.label === suggestedBusinessFrontLabel)
    return item ? { name: item.value, description: item.label } : null
  }, [agencyFlows, suggestedBusinessFrontLabel])

  const suggestedPaymentResponsible = useMemo(() => {
    return paymentResponsibleOptions?.find(
      item => item?.status === suggestedPaymentResponsibleStatus
    )
  }, [paymentResponsibleOptions, suggestedPaymentResponsibleStatus])

  const categories = useMemo(() => {
    if (!serviceOrder?.category || !categoryList) return []
    const serviceCategoryIds = serviceOrder.category.toJS()?.map(cat => cat.id) || []
    return categoryList.filter(item => serviceCategoryIds.includes(item.id))
  }, [serviceOrder?.category, categoryList])

  const getCategoryNames = useMemo(() => {
    const categoriesToShow = selectedCategories?.length > 0 ? selectedCategories : categories

    if (categoriesToShow?.length > 1) {
      const sortedCategories = [...categoriesToShow].sort((a, b) => (a.name > b.name ? 1 : -1))
      const mainCategoryName = sortedCategories?.[0]?.name ?? 'Categoria'
      const extraCategories = sortedCategories?.slice(1)

      const subcategories = extraCategories.map(item => item.name).join(', ')

      return `${mainCategoryName}, ${subcategories}`
    }
    return categoriesToShow?.[0]?.name ?? 'Categoria'
  }, [categories, selectedCategories])

  const handleUpdateCategories = useCallback(() => {
    if (selectedCategories.length === 0) {
      showDialog({
        type: 'warning',
        subject: 'Atenção',
        description: 'Selecione antes a categoria do chamado.',
        labelApprove: 'Ok',
        onApprove: closeDialog,
      })
    } else {
      setOpenCategoriesModal(false)

      const categoriesIds = selectedCategories.map(category => category.id)
      setCategoriesToUpdate(categoriesIds)
      methods.setValue('triage.categoriesName', getCategoryNames, { shouldDirty: true })
    }
  }, [dispatch, selectedCategories, getCategoryNames])

  const handleSelectChange = useCallback(
    (event, onChange) => {
      const priorValue = methods.getValues(event.target.name)
      if (priorValue !== '' && event.target.value === '') {
        showToast({
          message: 'Uma vez preenchido este campo, ele não pode mais ser anulado.',
          type: 'error',
        })
        methods.setValue(event.target.name, priorValue)
      } else {
        onChange(event)
      }
    },
    [methods, showToast]
  )

  const getAgencyFlowOptions = (agencyParam, businessFront) => {
    const currentFlow = businessFront || {}
    const options =
      agencyParam?.flows?.map(option => ({
        value: option.name,
        label: option.description,
        disabled: false,
      })) || []

    if (currentFlow.name && !options.some(opt => opt.value === currentFlow.name)) {
      options.push({
        value: currentFlow.name,
        label: currentFlow.description,
        disabled: true,
      })
    }
    options.sort((a, b) => a.label.localeCompare(b.label))
    return options
  }

  const businessFrontHelpText = useMemo(() => {
    const currentFlowValue = methods.getValues('triage.flow')
    return businessFrontList?.find(item => item.name === currentFlowValue)?.helpText
  }, [businessFrontList, methods.watch('triage.flow')])

  useEffect(() => {
    if (!agencyFlows && serviceOrderData?.agency) {
      const options = getAgencyFlowOptions(
        serviceOrderData?.agency,
        serviceOrderData?.businessFront
      )
      dispatch(updateServiceOrderFlowsOptions(options))
      dispatch(getBusinessFronts())
    }
  }, [dispatch, serviceOrderData, agencyFlows, getAgencyFlowOptions])

  useEffect(() => {
    methods.reset({
      triage: {
        categoriesName: getCategoryNames,
        iaSuggestedCategories: suggestedCategories,
        acceptCategorySuggestion: null,
        aiCategoryErrorExplanation: '',
        priority: priority || '',
        iaSuggestedPriority: suggestedPriority || '',
        acceptPrioritySuggestion: null,
        aiPriorityErrorExplanation: '',
        flow: notShowValue ? '' : flow?.name,
        iaSuggestedFlow: suggestedFlow?.name || '',
        acceptFlowSuggestion: null,
        aiFlowErrorExplanation: '',
        aiPaymentSuggestionErrorExplanation: '',
        acceptPaymentResponsibleSuggestion: null,
        aiPaymentSuggestion: suggestedPaymentResponsible?.status || '',
        contractStartDate: serviceOrder?.contractStartDate || null,
      },
    })
  }, [
    categories,
    priority,
    suggestedPriority,
    suggestedCategories,
    suggestedFlow,
    suggestedPaymentResponsible,
  ])

  const handleBlur = () => {
    if (!contractStartDate || !serviceOrder?.createdAt) {
      setTextDate('')
      return
    }

    const now = moment(serviceOrder?.createdAt)
    const start = moment(contractStartDate)

    if (!start.isValid() || !now.isValid()) {
      setTextDate('')
      return
    }

    const years = now.diff(start, 'years')
    const months = now.diff(start.clone().add(years, 'years'), 'months')

    if (years <= 0 && months <= 0) {
      setTextDate('0 mês(es)')
    } else if (years === 0) {
      setTextDate(`${months} mês(es)`)
    } else {
      setTextDate(`${years} ano(s) e ${months} mês(es)`)
    }
  }

  useEffect(() => {
    if (payer === '') {
      if (prevPaymentResponsible && prevPaymentResponsible !== '') {
        methods.setValue('paymentResponsible', prevPaymentResponsible)

        showToast({
          message: 'Uma vez preenchido este campo, ele não pode mais ser anulado.',
          type: 'error',
        })
      }
    } else {
      setPrevPaymentResponsible(payer)
    }
  }, [payer, prevPaymentResponsible])

  const suggestionMap = {
    'triage.categoriesName': suggestedCategories,
    'triage.priority': suggestedPriority,
    'triage.flow': suggestedFlow?.name,
    paymentResponsible: suggestedPaymentResponsible?.status,
  }

  const handleAcceptSuggestion = fieldName => {
    const suggestion = suggestionMap[fieldName] ?? null

    if (!suggestion) {
      showToast({
        message: 'A IA não soube o que sugerir, não sendo possível aceitar sua sugestão.',
        type: 'error',
      })
      return false
    }
    if (fieldName === 'triage.categoriesName') {
      const acceptedCategories = categoryList?.filter(cat =>
        suggestedCategoryNames?.includes(cat?.name)
      )

      setCategoriesToUpdate(acceptedCategories?.map(cat => cat?.id))
      setSelectedCategories(acceptedCategories)
    }

    methods.setValue(fieldName, suggestion)
    return true
  }

  const returnEmpty = useMemo(() => {
    if (isAITriageOn) return <div />
    return <></>
  }, [isAITriageOn])

  const closeAIExplanationDrawer = useCallback(() => {
    setAiExplanationState(DEFAULT_AI_EXPLANATION_STATE)
  }, [])

  const openAIExplanationDrawer = useCallback(
    ({ confidence, fieldKey, fieldLabel, suggestedValue }) => {
      const badgeConfig = getConfidenceBadgeConfig(confidence, confidenceThresholds)

      if (!badgeConfig) return

      const displayValue = Array.isArray(suggestedValue)
        ? suggestedValue.filter(Boolean).join(', ')
        : suggestedValue

      setAiExplanationState({
        open: true,
        fieldLabel,
        suggestedValue: displayValue || 'Sem sugestão da IA',
        confidenceLabel: badgeConfig.label,
        backgroundColor: badgeConfig.backgroundColor,
        textColor: badgeConfig.textColor,
        triggers: getAISuggestionTriggers(aiSuggestions, fieldKey),
        reasoningSummary: getAISuggestionReasoningSummary(aiSuggestions, fieldKey),
      })
    },
    [aiSuggestions, confidenceThresholds]
  )

  const renderConfidenceBadge = ({ confidence, fieldKey, fieldLabel, suggestedValue }) => {
    const badgeConfig = getConfidenceBadgeConfig(confidence, confidenceThresholds)

    if (!badgeConfig) return null

    return (
      <button
        type="button"
        className={styles.confidenceToast}
        style={{
          backgroundColor: badgeConfig.backgroundColor,
          color: badgeConfig.textColor,
        }}
        aria-label={`Abrir explicação da IA - ${fieldLabel}`}
        onClick={() =>
          openAIExplanationDrawer({
            confidence,
            fieldKey,
            fieldLabel,
            suggestedValue,
          })
        }
      >
        {badgeConfig.label}
      </button>
    )
  }

  return (
    <>
      <PreviewComponent
        images={serviceOrderAttachmentsMedia}
        open={previewState.open}
        selectedIndex={previewState.selectedIndex}
        setPreviewState={setPreviewState}
      />
      <SwipeableDrawer
        component="section"
        anchor="right"
        open={aiExplanationState.open}
        onClose={closeAIExplanationDrawer}
        onOpen={() => {}}
        className={styles.aiExplanationDrawerContainer}
        TransitionComponent={AIExplanationTransition}
      >
        <div className={styles.aiExplanationDrawer}>
          <div className={styles.aiExplanationHeader}>
            <div className={styles.aiExplanationHeaderTitle}>
              <div className={styles.aiExplanationIconWrapper}>
                <Edit2 className={styles.aiExplanationIcon} />
              </div>
              <Typography component="h2" variant="h5" className={styles.aiExplanationTitle}>
                Explicação da IA - {aiExplanationState.fieldLabel}
              </Typography>
            </div>
            <IconButton onClick={closeAIExplanationDrawer} aria-label="Fechar explicação da IA">
              <CloseCircle
                color={Theme.Colors.Primary.Base}
                className={styles.aiExplanationCloseIcon}
              />
            </IconButton>
          </div>

          <Divider className={styles.aiExplanationDivider} />

          <div className={styles.aiExplanationContent}>
            <section className={styles.aiExplanationSection}>
              <Typography className={styles.aiExplanationSectionTitle}>Conclusão</Typography>
              <div className={styles.aiExplanationConclusionCard}>
                <Typography className={styles.aiExplanationConclusionLabel}>
                  {aiExplanationState.fieldLabel}
                </Typography>
                <Typography className={styles.aiExplanationConclusionValue}>
                  {`"${aiExplanationState.suggestedValue}"`}
                </Typography>
                <span
                  className={styles.aiExplanationConfidenceBadge}
                  style={{
                    backgroundColor: aiExplanationState.backgroundColor,
                    color: aiExplanationState.textColor,
                  }}
                >
                  {aiExplanationState.confidenceLabel}
                </span>
              </div>
            </section>

            <section className={styles.aiExplanationSection}>
              <Typography className={styles.aiExplanationSectionTitle}>Gatilhos da IA</Typography>
              {aiExplanationState.triggers?.length > 0 ? (
                <div className={styles.aiExplanationTriggerList}>
                  {aiExplanationState.triggers.map(trigger => (
                    <span key={trigger} className={styles.aiExplanationTriggerTag}>
                      {trigger}
                    </span>
                  ))}
                </div>
              ) : (
                <Typography className={styles.aiExplanationEmptyText}>
                  Nenhum gatilho foi informado pela IA para este campo.
                </Typography>
              )}
            </section>

            <section className={styles.aiExplanationSection}>
              <Typography className={styles.aiExplanationSectionTitle}>
                Resumo do Raciocínio
              </Typography>
              <Typography className={styles.aiExplanationReasoningText}>
                {aiExplanationState.reasoningSummary ||
                  'A IA não retornou um resumo de raciocínio para este campo.'}
              </Typography>
            </section>
          </div>
        </div>
      </SwipeableDrawer>
      {cameFromSelectTradesman && (
        <Alert
          severity="warning"
          title="Para selecionar prestador, você deve preencher os campos de Triagem abaixo."
        />
      )}
      <form
        id="triageForm"
        onSubmit={e => {
          e.preventDefault()
          checkUserPermission({
            permission: PERMISSIONS.TRIAGE,
            permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
            functionAccess: () => handleSubmit(onSubmit, onInvalidSubmit)(),
          })
        }}
      >
        {serviceOrder && (
          <Grid className={styles.container}>
            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Dados do chamado</InputLabel>
              <Grid className={styles.serviceOrderContent}>
                <div>
                  <InputLabel variant="standard" className={styles.textAreaLabel}>
                    Descrição do chamado
                  </InputLabel>
                  <div className={styles.serviceOrderDescription}>
                    {serviceOrderDescription || 'Sem descrição informada.'}
                  </div>
                </div>

                <div className={styles.serviceOrderAttachments}>
                  <Attachments
                    label="Imagens e vídeos"
                    readOnly
                    files={serviceOrderAttachmentsMedia}
                    downloadable
                    accept={MEDIA_ACCEPT}
                    onDrop={() => null}
                    onRemoveItem={() => null}
                    onItemClick={index =>
                      setPreviewState({
                        open: true,
                        selectedIndex: index,
                      })
                    }
                  />
                  <Attachments
                    label="Arquivos PDF"
                    content="files"
                    readOnly
                    downloadable
                    files={serviceOrderAttachmentsPdf}
                    accept={PDF_ACCEPT}
                    onDrop={() => null}
                    onRemoveItem={() => null}
                    onItemClick={index =>
                      window.open(
                        serviceOrderAttachmentsPdf[index]?.file,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  />
                </div>
              </Grid>
            </div>
            <div className={styles.subcontainer}>
              <InputLabel className={styles.label}>Dados da Triagem</InputLabel>
              <div className={styles.wrapper}>
                <div>
                  <InputLabel variant="standard" className={styles.textAreaLabel}>
                    Categorias
                    {cameFromSelectTradesman && <span className={styles.asterisk}>*</span>}
                  </InputLabel>

                  <Controller
                    name="triage.categoriesName"
                    control={control}
                    rules={{
                      required: cameFromSelectTradesman && 'Esse campo é obrigatório',
                    }}
                    render={({ value, onChange, onBlur }) => (
                      <TextField
                        className={[styles.textArea, styles.thinErrorUnderline]}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        multiline
                        variant="outlined"
                        maxRows={15}
                        minRows={3}
                        disabled
                        error={!!errors?.triage?.categoriesName}
                        helperText={errors?.triage?.categoriesName?.message}
                      />
                    )}
                  />
                </div>

                {isAITriageOn && (
                  <div>
                    <InputLabel variant="standard" className={styles.textAreaLabel}>
                      IA - Sugestão de Categorias
                      {renderConfidenceBadge({
                        confidence: categoryConfidence,
                        fieldKey: 'category',
                        fieldLabel: 'Categorias',
                        suggestedValue: suggestedCategories,
                      })}
                    </InputLabel>
                    <Controller
                      name="triage.iaSuggestedCategories"
                      control={control}
                      render={({ value }) => (
                        <TextField
                          placeholder="Sem sugestão da IA"
                          className={styles.textArea}
                          value={value}
                          multiline
                          variant="outlined"
                          maxRows={15}
                          minRows={3}
                          disabled
                        />
                      )}
                    />
                    <div className={styles.radioGroup}>
                      <Controller
                        name="triage.acceptCategorySuggestion"
                        control={control}
                        render={({ value, onChange }) => (
                          <>
                            <FormControlLabel
                              label="Aceitar sugestão"
                              className={styles.formLabel}
                              control={
                                <Radio
                                  checked={value === true}
                                  onChange={() => {
                                    onChange(true)
                                    handleAcceptSuggestion('triage.categoriesName')
                                  }}
                                />
                              }
                            />
                            <FormControlLabel
                              label="Recusar sugestão"
                              className={styles.formLabel}
                              control={
                                <Radio checked={value === false} onChange={() => onChange(false)} />
                              }
                            />
                          </>
                        )}
                      />
                    </div>
                  </div>
                )}
                {acceptCategory === false ? (
                  <div>
                    <InputLabel variant="standard" className={styles.textAreaLabel}>
                      Por que você recusa?
                      {acceptCategory === false && <span className={styles.required}>*</span>}
                    </InputLabel>
                    <Controller
                      name="triage.aiCategoryErrorExplanation"
                      control={control}
                      rules={{
                        required: 'Esse campo é obrigatório',
                      }}
                      render={({ value, onChange, onBlur }) => (
                        <TextField
                          className={styles.textArea}
                          value={value}
                          onChange={onChange}
                          multiline
                          variant="outlined"
                          onBlur={onBlur}
                          maxRows={15}
                          minRows={3}
                          error={!!errors?.triage?.aiCategoryErrorExplanation}
                        />
                      )}
                    />
                    <FormHelperText
                      className={styles.errorMessage}
                      error={!!errors?.triage?.aiCategoryErrorExplanation}
                    >
                      {errors?.triage?.aiCategoryErrorExplanation?.message}
                    </FormHelperText>
                  </div>
                ) : (
                  returnEmpty
                )}
                <div className={styles.floatingEditButton}>
                  <IconButton
                    className={styles.iconButton}
                    onClick={() => setOpenCategoriesModal(true)}
                  >
                    <Edit2 className={styles.editIcon} />
                  </IconButton>
                </div>

                <div>
                  <InputLabel variant="standard" className={styles.textAreaLabel}>
                    Prioridade
                    {cameFromSelectTradesman && <span className={styles.asterisk}>*</span>}
                  </InputLabel>

                  <Controller
                    control={control}
                    name="triage.priority"
                    defaultValue={priority || ''}
                    rules={{
                      required: cameFromSelectTradesman && 'Esse campo é obrigatório',
                    }}
                    render={({ name, value, onChange, onBlur }) => (
                      <Select
                        isPriorityOptions
                        value={value}
                        onChange={e => handleSelectChange(e, onChange)}
                        className={[styles.select, styles.thinErrorUnderline]}
                        name={name}
                        onBlur={onBlur}
                        emptyValue="Selecione"
                        IconComponent={ArrowDropDownIcon}
                        error={!!errors?.triage?.priority}
                        helperText={errors?.triage?.priority?.message}
                      />
                    )}
                  />
                </div>
                {isAITriageOn && (
                  <div>
                    <InputLabel variant="standard" className={styles.textAreaLabel}>
                      IA - Sugestão de Prioridade
                      {renderConfidenceBadge({
                        confidence: priorityConfidence,
                        fieldKey: 'priority',
                        fieldLabel: 'Prioridade',
                        suggestedValue: suggestedPriorityLabel,
                      })}
                    </InputLabel>
                    <Controller
                      control={control}
                      name="triage.iaSuggestedPriority"
                      // defaultValue={suggestedPriority ?? ''}
                      defaultValue={suggestedPriority ?? ''}
                      render={({ name, value, onChange }) => (
                        <Select
                          emptyValue="Sem sugestão da IA"
                          displayEmpty
                          isPriorityOptions
                          value={value}
                          className={styles.select}
                          onChange={e => handleSelectChange(e, onChange)}
                          name={name}
                          IconComponent={ArrowDropDownIcon}
                          disabled
                        />
                      )}
                    />
                    <div className={styles.radioGroup}>
                      <Controller
                        name="triage.acceptPrioritySuggestion"
                        control={control}
                        render={({ value, onChange }) => (
                          <>
                            <FormControlLabel
                              className={styles.formLabel}
                              label="Aceitar sugestão"
                              control={
                                <Radio
                                  checked={value === true}
                                  onChange={() => {
                                    onChange(true)
                                    handleAcceptSuggestion('triage.priority')
                                  }}
                                />
                              }
                            />
                            <FormControlLabel
                              className={styles.formLabel}
                              label="Recusar sugestão"
                              control={
                                <Radio checked={value === false} onChange={() => onChange(false)} />
                              }
                            />
                          </>
                        )}
                      />
                    </div>
                  </div>
                )}
                {acceptPriority === false ? (
                  <div>
                    <InputLabel variant="standard" className={styles.textAreaLabel}>
                      Por que você recusa?
                      {acceptPriority === false && <span className={styles.required}>*</span>}
                    </InputLabel>
                    <Controller
                      name="triage.aiPriorityErrorExplanation"
                      control={control}
                      rules={{
                        required: 'Esse campo é obrigatório',
                      }}
                      render={({ value, onChange, onBlur }) => (
                        <TextField
                          className={styles.textArea}
                          value={value}
                          onChange={onChange}
                          multiline
                          variant="outlined"
                          maxRows={15}
                          minRows={3}
                          onBlur={onBlur}
                          error={!!errors?.triage?.aiPriorityErrorExplanation}
                        />
                      )}
                    />
                    <FormHelperText
                      className={styles.errorMessage}
                      error={!!errors?.triage?.aiPriorityErrorExplanation}
                    >
                      {errors?.triage?.aiPriorityErrorExplanation?.message}
                    </FormHelperText>
                  </div>
                ) : (
                  returnEmpty
                )}

                {agencyFlows?.length > 0 && (
                  <>
                    <div>
                      <InputLabel variant="standard" className={styles.textAreaLabel}>
                        Fluxo
                        {cameFromSelectTradesman && <span className={styles.asterisk}>*</span>}
                      </InputLabel>

                      <Controller
                        control={control}
                        name="triage.flow"
                        defaultValue={notShowValue ? '' : flow?.name}
                        rules={{
                          required: cameFromSelectTradesman && 'Esse campo é obrigatório',
                        }}
                        render={({ name, value, onChange, onBlur }) => (
                          <div>
                            <Select
                              flowsOptions={agencyFlows}
                              isFlowsOptionField
                              className={[styles.select, styles.thinErrorUnderline]}
                              value={value}
                              onChange={e => handleSelectChange(e, onChange)}
                              name={name}
                              onBlur={onBlur}
                              emptyValue="Selecione"
                              IconComponent={ArrowDropDownIcon}
                              error={!!errors?.triage?.flow}
                              helperText={errors?.triage?.flow?.message}
                            />
                            {businessFrontHelpText && (
                              <FormHelperText className={styles.flowHelpMessage}>
                                {businessFrontHelpText}
                              </FormHelperText>
                            )}
                          </div>
                        )}
                      />
                    </div>
                    {isAITriageOn && (
                      <div>
                        <InputLabel variant="standard" className={styles.textAreaLabel}>
                          IA - Sugestão de Fluxo
                          {renderConfidenceBadge({
                            confidence: flowConfidence,
                            fieldKey: 'businessFront',
                            fieldLabel: 'Fluxo',
                            suggestedValue: suggestedBusinessFrontLabel,
                          })}
                        </InputLabel>
                        <Controller
                          control={control}
                          name="triage.iaSuggestedFlow"
                          defaultValue={suggestedFlow?.name ?? ''}
                          render={({ name, value, onChange }) => (
                            <Select
                              flowsOptions={agencyFlows}
                              emptyValue="Sem sugestão da IA"
                              displayEmpty
                              isFlowsOptionField
                              className={styles.select}
                              value={value}
                              onChange={e => handleSelectChange(e, onChange)}
                              name={name}
                              IconComponent={ArrowDropDownIcon}
                              disabled
                            />
                          )}
                        />
                        <div className={styles.radioGroup}>
                          <Controller
                            name="triage.acceptFlowSuggestion"
                            control={control}
                            render={({ value, onChange }) => (
                              <>
                                <FormControlLabel
                                  label="Aceitar sugestão"
                                  className={styles.formLabel}
                                  control={
                                    <Radio
                                      checked={value === true}
                                      onChange={() => {
                                        onChange(true)
                                        handleAcceptSuggestion('triage.flow')
                                      }}
                                    />
                                  }
                                />
                                <FormControlLabel
                                  label="Recusar sugestão"
                                  className={styles.formLabel}
                                  control={
                                    <Radio
                                      checked={value === false}
                                      onChange={() => onChange(false)}
                                    />
                                  }
                                />
                              </>
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
                {acceptFlow === false ? (
                  <div>
                    <InputLabel variant="standard" className={styles.textAreaLabel}>
                      Por que você recusa?
                      {acceptFlow === false && <span className={styles.required}>*</span>}
                    </InputLabel>
                    <Controller
                      name="triage.aiFlowErrorExplanation"
                      control={control}
                      rules={{
                        required: 'Esse campo é obrigatório',
                      }}
                      render={({ value, onChange, onBlur }) => (
                        <TextField
                          className={styles.textArea}
                          value={value}
                          onChange={onChange}
                          multiline
                          variant="outlined"
                          maxRows={15}
                          minRows={3}
                          onBlur={onBlur}
                          error={!!errors?.triage?.aiFlowErrorExplanation}
                        />
                      )}
                    />
                    <FormHelperText
                      className={styles.errorMessage}
                      error={!!errors?.triage?.aiFlowErrorExplanation}
                    >
                      {errors?.triage?.aiFlowErrorExplanation?.message}
                    </FormHelperText>
                  </div>
                ) : (
                  returnEmpty
                )}

                <div className={styles.selectWrapper}>
                  <SelectField
                    key="payer"
                    label="Responsabilidade pelo pagamento"
                    name="paymentResponsible"
                    defaultValue={propertyInfo?.paymentResponsible || ''}
                    control={control}
                    options={paymentResponsibleOptions}
                    getValue={item => item.status}
                    getLabel={item => item.option}
                    emptyValue="Selecione"
                    required={false}
                    displayEmpty={false}
                    labelClasses={styles.textAreaLabel}
                  />
                </div>
                {isAITriageOn && (
                  <div className={styles.selectWrapper}>
                    <SelectField
                      id="suggestedPayer"
                      label="IA - Sugestão de Responsabilidade pelo pagamento"
                      labelAddon={renderConfidenceBadge({
                        confidence: paymentResponsibleConfidence,
                        fieldKey: 'paymentResponsible',
                        fieldLabel: 'Responsabilidade pelo pagamento',
                        suggestedValue:
                          suggestedPaymentResponsible?.option || suggestedPaymentResponsibleStatus,
                      })}
                      name="triage.aiPaymentSuggestion"
                      defaultValue={suggestedPaymentResponsible?.status || ''}
                      control={control}
                      options={paymentResponsibleOptions}
                      getValue={item => item.status}
                      getLabel={item => item.option}
                      disabled
                      labelClasses={styles.textAreaLabel}
                      emptyValue="Sem sugestão da IA"
                      displayEmpty
                    />

                    <div className={styles.radioGroup}>
                      <Controller
                        name="triage.acceptPaymentResponsibleSuggestion"
                        control={control}
                        render={({ value, onChange }) => (
                          <>
                            <FormControlLabel
                              className={styles.formLabel}
                              label="Aceitar sugestão"
                              control={
                                <Radio
                                  checked={value === true}
                                  onChange={() => {
                                    onChange(true)
                                    handleAcceptSuggestion('paymentResponsible')
                                  }}
                                />
                              }
                            />
                            <FormControlLabel
                              className={styles.formLabel}
                              label="Recusar sugestão"
                              control={
                                <Radio checked={value === false} onChange={() => onChange(false)} />
                              }
                            />
                          </>
                        )}
                      />
                    </div>
                  </div>
                )}
                {acceptPaymentResponsible === false ? (
                  <div>
                    <InputLabel variant="standard" className={styles.textAreaLabel}>
                      Por que você recusa?
                      {acceptPaymentResponsible === false && (
                        <span className={styles.required}>*</span>
                      )}
                    </InputLabel>
                    <Controller
                      name="triage.aiPaymentSuggestionErrorExplanation"
                      control={control}
                      rules={{
                        required: 'Esse campo é obrigatório',
                      }}
                      render={({ value, onChange, onBlur }) => (
                        <TextField
                          className={styles.textArea}
                          value={value}
                          onChange={onChange}
                          multiline
                          variant="outlined"
                          maxRows={15}
                          minRows={3}
                          onBlur={onBlur}
                          error={!!errors?.triage?.aiPaymentSuggestionErrorExplanation}
                        />
                      )}
                    />
                    <FormHelperText
                      className={styles.errorMessage}
                      error={!!errors?.triage?.aiPaymentSuggestionErrorExplanation}
                    >
                      {errors?.triage?.aiPaymentSuggestionErrorExplanation?.message}
                    </FormHelperText>
                  </div>
                ) : (
                  returnEmpty
                )}

                <div>
                  <div className={styles.dateRow}>
                    <Controller
                      name="triage.contractStartDate"
                      control={control}
                      defaultValue=""
                      rules={{
                        validate: value => {
                          if (!value) return true
                          return moment(value).isValid() || 'Insira uma data válida'
                        },
                      }}
                      render={({ value, onChange, onBlur }) => (
                        <div className={styles.dateColumn}>
                          <DatePicker
                            value={value}
                            onChange={onChange}
                            required={false}
                            label="Data do início do contrato"
                            variant="inline"
                            format="dd/MM/yyyy"
                            placeholder="DD/MM/AAAA"
                            invalidDateMessage="Insira uma data válida"
                            refuse={/[^\d\\.]+/gi}
                            className={styles.dateContent}
                            mask="__/__/____"
                            renderInput={params => (
                              <TextField
                                {...params}
                                variant="standard"
                                InputLabelProps={{ shrink: true }}
                                onBlur={event => {
                                  onBlur()
                                  handleBlur?.(event)
                                }}
                              />
                            )}
                            error={!!errors?.triage?.contractStartDate}
                          />
                          <FormHelperText
                            className={styles.errorMessage}
                            error={!!errors?.triage?.contractStartDate}
                          >
                            {errors?.triage?.contractStartDate?.message}
                          </FormHelperText>
                        </div>
                      )}
                    />
                    {textDate && <Typography style={{ marginTop: '8px' }}>{textDate}</Typography>}
                  </div>
                </div>
              </div>
            </div>
            <Grid className={styles.subcontainer}>
              <InputLabel className={styles.label}>Dados do(a) {ownerTerm}</InputLabel>
              <Grid className={styles.wrapper}>
                <Grid className={styles.input}>
                  <BasicInput
                    label={`Nome do(a) ${ownerTerm}`}
                    name="propertyName"
                    labelClasses={styles.inputLabel}
                    defaultValue={propertyInfo?.propertyName || ''}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <MaskBasicInput
                    label={`CPF/CNPJ do(a) ${ownerTerm}`}
                    name="cpfOrCnpj"
                    mask={cpfCnpjMaskSwitch(cpfOrCnpjWatch)}
                    number
                    labelClasses={styles.inputLabel}
                    defaultValue={cpfCnpjMask(propertyInfo?.cpfOrCnpj) || ''}
                    rules={{
                      pattern: { value: CPF_CNPJ_REGEX, message: 'CPF ou CNPJ inválido' },
                      validate: {
                        checkCpfCnpj: v => validateCPFCNPJ(v) || '',
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid className={styles.wrapper}>
                <Grid className={styles.input}>
                  <BasicInput
                    label={`E-mail do(a) ${ownerTerm}`}
                    name="propertyEmail"
                    labelClasses={styles.inputLabel}
                    defaultValue={propertyInfo?.propertyEmail || ''}
                    rules={{
                      pattern: { value: EMAIL_REGEX, message: 'E-mail inválido' },
                    }}
                  />
                </Grid>

                <Grid className={styles.input}>
                  <MaskBasicInput
                    label={`WhatsApp do(a) ${ownerTerm}`}
                    name="propertyWhatsapp"
                    mask="+55 (99) 9 9999-9999"
                    rules={{
                      pattern: { value: PHONE_REGEX, message: 'WhatsApp inválido' },
                    }}
                    labelClasses={styles.inputLabel}
                    defaultValue={
                      propertyInfo?.propertyWhatsapp
                        ? includeDDI(propertyInfo?.propertyWhatsapp)
                        : ''
                    }
                  />
                </Grid>
                <Grid className={styles.input}>
                  <Checkbox
                    label="Enviar atualizações pelo WhatsApp"
                    name="propertyWhatsappUpdate"
                    defaultValue={propertyInfo?.propertyWhatsappUpdate}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid className={styles.subcontainer}>
              <InputLabel className={styles.label}>Dados do(a) {tenantTerm}</InputLabel>
              <Grid className={styles.wrapper}>
                <Grid className={styles.input}>
                  <BasicInput
                    label={`Nome do(a) ${tenantTerm}`}
                    name="tenantName"
                    labelClasses={styles.inputLabel}
                    defaultValue={propertyInfo?.tenantName || ''}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <MaskBasicInput
                    label={`CPF/CNPJ do(a) ${tenantTerm}`}
                    name="tenantCpfOrCnpj"
                    mask={cpfCnpjMaskSwitch(tenantCpfOrCnpjWatch)}
                    number
                    labelClasses={styles.inputLabel}
                    defaultValue={cpfCnpjMask(propertyInfo?.tenantCpfOrCnpj) || ''}
                    rules={{
                      pattern: { value: CPF_CNPJ_REGEX, message: 'CPF ou CNPJ inválido' },
                      validate: {
                        checkCpfCnpj: v => validateCPFCNPJ(v) || '',
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid className={styles.wrapper}>
                <Grid className={styles.input}>
                  <BasicInput
                    label={`E-mail do(a) ${tenantTerm}`}
                    name="tenantEmail"
                    labelClasses={styles.inputLabel}
                    defaultValue={propertyInfo?.tenantEmail || ''}
                    rules={{
                      pattern: { value: EMAIL_REGEX, message: 'E-mail inválido' },
                    }}
                  />
                </Grid>

                <Grid className={styles.input}>
                  <MaskBasicInput
                    label={`WhatsApp do(a) ${tenantTerm}`}
                    name="tenantWhatsapp"
                    mask="+55 (99) 9 9999-9999"
                    rules={{
                      pattern: { value: PHONE_REGEX, message: 'WhatsApp inválido' },
                    }}
                    labelClasses={styles.inputLabel}
                    defaultValue={propertyInfo?.tenantWhatsapp || ''}
                  />
                </Grid>
                <Grid className={styles.input}>
                  <Checkbox
                    label="Enviar atualizações pelo WhatsApp"
                    name="tenantWhatsappUpdate"
                    defaultValue={propertyInfo?.tenantWhatsappUpdate}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </form>
      <Modal.Root
        className={styles.rootClassification}
        open={openCategoriesModal}
        onClose={() => {
          setOpenCategoriesModal(false)
          setSelectedCategories([])
        }}
      >
        <Modal.TitleModal title="Edição de categorias" />
        <Modal.Content className={styles.modalContent}>
          <Grid className={styles.treeWrapper}>
            <MultiSelectedTree
              data={categoryList}
              categories={selectedCategories.length > 0 ? selectedCategories : categories}
              onSelectedItemsChange={handleSelectedItemsChange}
              onlyParent
            />
          </Grid>
        </Modal.Content>
        <Modal.Actions className={styles.actions}>
          <Modal.ButtonRed
            onClick={() => {
              setOpenCategoriesModal(false)
              setSelectedCategories([])
            }}
          >
            Cancelar
          </Modal.ButtonRed>
          <Modal.ButtonFullBlue onClick={handleUpdateCategories}>Salvar</Modal.ButtonFullBlue>
        </Modal.Actions>
      </Modal.Root>
    </>
  )
}

export default TriageForm
